// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { Arpaymentmethods } from '@gql/base-types';
import { gatewayChargeTotal, type PaymentMethodType } from '@hopdrive/sdk-ar';
import { getMonetaryValue } from '@services/formatService';

import { css } from '@emotion/css';
import { Grid, Link, MenuItem, TextField, Theme, Typography, useTheme } from '@mui/material';

import Spacer from '@components/Utils/Spacer';

// TYPES ---------------------------------------------------------------- //

type PayNowContentProps = {
  input?: any;
  /** sdk-ar's amount to charge for the invoice's charges. Null while it loads. */
  amountToCharge: number | null;
  paymentMethods?: Arpaymentmethods[];
  paymentMethod?: Arpaymentmethods;
  onPaymentMethodChange: (method?: Arpaymentmethods) => void;
};

// COMPONENT ---------------------------------------------------------------- //

const PayNowContent = ({ input, amountToCharge, paymentMethods, paymentMethod, onPaymentMethodChange }: PayNowContentProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  // What the payment path collects: the amount to charge plus the card fee,
  // both from sdk-ar. Null means the amount is still loading, so neither
  // figure shows yet.
  const chargeTotal =
    amountToCharge != null ? gatewayChargeTotal(amountToCharge, paymentMethod?.type as PaymentMethodType | undefined) : null;
  const processingFee = chargeTotal?.processingFee ?? 0;
  const amountDue = chargeTotal?.total ?? null;

  const handleMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event?.target?.value);
    const foundMethod = paymentMethods?.find(m => m?.id === value);
    if (onPaymentMethodChange) onPaymentMethodChange(foundMethod || undefined);
  };

  return (
    <>
      {paymentMethods?.length ? (
        <>
          <Spacer size={1} />

          <TextField
            fullWidth
            required
            select
            label='Payment Method'
            helperText={
              paymentMethod && paymentMethod.gateway !== `stripe`
                ? `This method was saved with our previous payment processor and must be entered again under Manage Payment Methods before it can be charged.`
                : paymentMethod?.type === `card`
                  ? `NOTE: A processing fee will be included when using a card.`
                  : paymentMethod?.type === `ach` || paymentMethod?.type === `echeck`
                    ? `A bank payment is submitted today and settles in a few business days.`
                    : ``
            }
            margin='none'
            variant='outlined'
            value={paymentMethod?.id || ``}
            onChange={handleMethodChange}
          >
            <MenuItem key={'Select Payment Method'} value={``}>
              <em>Select Payment Method</em>
            </MenuItem>

            {paymentMethods?.map(m => (
              // A method saved under the previous processor cannot be charged; it stays listed,
              // greyed, so the dealer sees it needs re-entering rather than wondering where it went.
              <MenuItem key={m?.id} value={m?.id} disabled={m.gateway !== `stripe`}>
                {m.name}
                {m.primary === true ? ` (Primary)` : null}
                {m.gateway !== `stripe` ? ` — needs re-entry` : null}
              </MenuItem>
            ))}
          </TextField>

          <Spacer />

          <Typography sx={{ fontSize: `12px` }}>
            <Link href='/billing-preferences'>Manage Payment Methods</Link>
          </Typography>

          <Spacer size={3} />
        </>
      ) : (
        <>
          <Typography sx={{ fontSize: `12px` }}>
            <Link href='/billing-preferences'>Enter a Payment Method to pay Invoice</Link>
          </Typography>

          <Spacer size={3} />
        </>
      )}

      <Grid container>
        {Number(input?.subtotal) && (Number(input?.discounted) || Number(input?.disputed)) ? (
          <>
            <Grid size={{ xs: 6 }}>
              <Typography sx={clsx.keyText}>Subtotal</Typography>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Typography sx={clsx.valText}>{getMonetaryValue(Number(input?.subtotal))}</Typography>
            </Grid>

            {Number(input?.discounted) ? (
              <>
                <Grid size={{ xs: 6 }}>
                  <Typography sx={clsx.keyText}>Discounted</Typography>
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <Typography sx={clsx.valText}>({getMonetaryValue(Number(input?.discounted))})</Typography>
                </Grid>
              </>
            ) : null}

            {Number(input?.disputed) ? (
              <>
                <Grid size={{ xs: 6 }}>
                  <Typography sx={clsx.keyText}>Disputed</Typography>
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <Typography sx={clsx.valText}>({getMonetaryValue(Number(input?.disputed))})</Typography>
                </Grid>
              </>
            ) : null}

            <div className={cls.lineBreak} />
          </>
        ) : null}

        {input?.total || input?.total === 0 ? (
          <>
            <Grid size={{ xs: 6 }}>
              <Typography sx={clsx.keyDemiText}>Total</Typography>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Typography sx={clsx.valDemiText}>{getMonetaryValue(input?.total)}</Typography>
            </Grid>
          </>
        ) : null}

        {paymentMethod?.type === `card` && processingFee ? (
          <>
            <Grid size={{ xs: 6 }}>
              <Typography sx={clsx.keyDemiText}>Processing Fee</Typography>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Typography sx={clsx.valDemiText}>{getMonetaryValue(processingFee)}</Typography>
            </Grid>
          </>
        ) : null}

        {input?.paid || input?.paid === 0 ? (
          <>
            <Grid size={{ xs: 6 }}>
              <Typography sx={clsx.keyDemiText}>Amount Paid</Typography>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Typography sx={clsx.valDemiText}>({getMonetaryValue(input?.paid)})</Typography>
            </Grid>
          </>
        ) : null}

        <div className={cls.lineBreak} />

        {amountDue || amountDue === 0 ? (
          <>
            <Grid size={{ xs: 6 }}>
              <Typography sx={clsx.keyGrandText}>Amount Due</Typography>
            </Grid>

            <Grid size={{ xs: 6 }}>
              <Typography sx={clsx.valGrandText}>{getMonetaryValue(amountDue)}</Typography>
            </Grid>
          </>
        ) : null}
      </Grid>
    </>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme: Theme) => ({
  lineBreak: css`
    width: 100%;
    border-bottom: 1px solid ${theme.palette.divider};
    margin: 8px 0;
  `,
});

const useSxStyles = (theme?: Theme) => ({
  keyText: {
    textAlign: 'left',
    fontSize: '16px',
    fontWeight: 400,
    color: theme?.palette?.text?.secondary,
  },
  valText: {
    textAlign: 'right',
    fontSize: '16px',
    fontWeight: 400,
    color: theme?.palette?.text?.secondary,
  },
  keyDemiText: {
    textAlign: 'left',
    fontSize: '16px',
    fontWeight: 400,
    color: theme?.palette?.text?.tertiary,
  },
  valDemiText: {
    textAlign: 'right',
    fontSize: '16px',
    fontWeight: 400,
    color: theme?.palette?.text?.tertiary,
  },
  keyBoldText: {
    textAlign: 'left',
    fontSize: '16px',
    fontWeight: 400,
  },
  valBoldText: {
    textAlign: 'right',
    fontSize: '16px',
    fontWeight: 400,
  },
  keyGrandText: {
    textAlign: 'left',
    fontSize: '20px',
    fontWeight: 600,
  },
  valGrandText: {
    textAlign: 'right',
    fontSize: '20px',
    fontWeight: 600,
  },
});

// EXPORT ---------------------------------------------------------------- //

export default PayNowContent;
